/**
 * SoleadLab 2.0 - Create / Edit Task Modal
 *
 * Modal form for creating a new task or editing an existing one.
 * Auto-fills title, description, priority, and impact from linked issues.
 */

import { useState, useEffect, useCallback } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import type {
  Task,
  TaskPriority,
  TaskStatus,
  Issue,
  EstimatedImpact,
} from '@/types/tasks';
import { SEVERITY_PRIORITY as severityPriorityMap } from '@/types/tasks';
import './CreateTaskModal.css';

// ── Props ──

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Partial<Task>) => void;
  linkedIssues?: Issue[];
  editingTask?: Task | null;
}

// ── Helpers ──

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'P1', label: 'P1 (Critical)' },
  { value: 'P2', label: 'P2 (Major)' },
  { value: 'P3', label: 'P3 (Minor)' },
  { value: 'P4', label: 'P4 (Info)' },
];

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'InProgress', label: 'In Progress' },
];

/**
 * Derive the highest priority from a set of linked issues.
 * Uses severity -> priority mapping: Critical=P1, Major=P2, Minor=P3, Info=P4.
 */
function deriveHighestPriority(issues: Issue[]): TaskPriority {
  if (issues.length === 0) return 'P3';
  const priorities = issues.map((i) => severityPriorityMap[i.severity]);
  const order: TaskPriority[] = ['P1', 'P2', 'P3', 'P4'];
  for (const p of order) {
    if (priorities.includes(p)) return p;
  }
  return 'P3';
}

/**
 * Build an auto-generated title from the linked issues.
 */
function buildAutoTitle(issues: Issue[]): string {
  if (issues.length === 0) return '';
  if (issues.length === 1) {
    const i = issues[0];
    const asset = i.assetPath.inverter || i.assetPath.string || i.assetPath.station || '';
    return `${i.category} — ${asset}`.replace(/ — $/, '');
  }
  const categories = [...new Set(issues.map((i) => i.category))];
  return `${categories.join(', ')} (${issues.length} issues)`;
}

/**
 * Build a description by joining recommended actions from linked issues.
 */
function buildAutoDescription(issues: Issue[]): string {
  if (issues.length === 0) return '';
  return issues.map((i) => i.recommendedAction).filter(Boolean).join('\n');
}

/**
 * Aggregate estimated impact across linked issues.
 */
function aggregateImpact(issues: Issue[]): EstimatedImpact {
  return issues.reduce<EstimatedImpact>(
    (acc, issue) => ({
      powerLossW: acc.powerLossW + (issue.estimatedImpact?.powerLossW ?? 0),
      energyLossKWh: acc.energyLossKWh + (issue.estimatedImpact?.energyLossKWh ?? 0),
      revenueLoss: acc.revenueLoss + (issue.estimatedImpact?.revenueLoss ?? 0),
    }),
    { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 }
  );
}

// ── Component ──

export default function CreateTaskModal({
  isOpen,
  onClose,
  onSave,
  linkedIssues = [],
  editingTask,
}: CreateTaskModalProps) {
  // ── Form State ──
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('P3');
  const [status, setStatus] = useState<TaskStatus>('Draft');
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [assignee, setAssignee] = useState('');
  const [checklist, setChecklist] = useState<string[]>([]);

  // ── Populate / Reset Form ──
  const resetForm = useCallback(() => {
    if (editingTask) {
      setTitle(editingTask.title);
      setDescription(editingTask.description);
      setPriority(editingTask.priority);
      setStatus(editingTask.status);
      setScheduleStart(editingTask.schedule?.start ?? '');
      setScheduleEnd(editingTask.schedule?.end ?? '');
      setAssignee(
        editingTask.assignees?.length
          ? editingTask.assignees.map((a) => a.userId).join(', ')
          : ''
      );
      setChecklist(editingTask.checklist?.length ? [...editingTask.checklist] : []);
    } else {
      // Auto-fill from linked issues
      setTitle(buildAutoTitle(linkedIssues));
      setDescription(buildAutoDescription(linkedIssues));
      setPriority(deriveHighestPriority(linkedIssues));
      setStatus('Draft');
      setScheduleStart('');
      setScheduleEnd('');
      setAssignee('');
      setChecklist([]);
    }
  }, [editingTask, linkedIssues]);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // ── Impact Preview ──
  const impact = editingTask?.estimatedImpact
    ? editingTask.estimatedImpact
    : aggregateImpact(linkedIssues);

  const hasImpact =
    impact.powerLossW > 0 || impact.energyLossKWh > 0 || impact.revenueLoss > 0;

  // ── Checklist Handlers ──
  const addChecklistItem = () => {
    setChecklist((prev) => [...prev, '']);
  };

  const updateChecklistItem = (index: number, value: string) => {
    setChecklist((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const removeChecklistItem = (index: number) => {
    setChecklist((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Save ──
  const handleSave = () => {
    if (!title.trim()) return;

    const taskData: Partial<Task> = {
      title: title.trim(),
      description: description.trim(),
      priority,
      status,
      schedule: {
        start: scheduleStart || undefined,
        end: scheduleEnd || undefined,
      },
      assignees: assignee.trim()
        ? assignee
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean)
            .map((userId) => ({ userId, role: '' }))
        : [],
      checklist: checklist.filter((item) => item.trim() !== ''),
      linkedIssueIds: linkedIssues.map((i) => i.issueId),
      estimatedImpact: impact,
    };

    // Preserve existing fields when editing
    if (editingTask) {
      taskData.taskId = editingTask.taskId;
      taskData.auditLog = editingTask.auditLog;
      taskData.sla = editingTask.sla;
      taskData.workReport = editingTask.workReport;
    }

    onSave(taskData);
  };

  // ── Render ──
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingTask ? 'Edit Task' : 'Create Task'}
      size="lg"
      footer={
        <div className="tp-modal-footer">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!title.trim()}>
            {editingTask ? 'Update Task' : 'Create Task'}
          </Button>
        </div>
      }
    >
      <div className="tp-modal-form">
        {/* Title */}
        <div className="tp-modal-field">
          <label className="tp-modal-label">
            Title <span className="tp-modal-required">*</span>
          </label>
          <input
            className="tp-modal-input"
            type="text"
            placeholder="Task title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Description */}
        <div className="tp-modal-field">
          <label className="tp-modal-label">Description</label>
          <textarea
            className="tp-modal-textarea"
            placeholder="Describe the task..."
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Priority + Status Row */}
        <div className="tp-modal-row">
          <div className="tp-modal-field tp-modal-field--half">
            <label className="tp-modal-label">Priority</label>
            <select
              className="tp-modal-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="tp-modal-field tp-modal-field--half">
            <label className="tp-modal-label">Status</label>
            <select
              className="tp-modal-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Schedule Row */}
        <div className="tp-modal-row">
          <div className="tp-modal-field tp-modal-field--half">
            <label className="tp-modal-label">Start Date/Time</label>
            <input
              className="tp-modal-input"
              type="datetime-local"
              value={scheduleStart}
              onChange={(e) => setScheduleStart(e.target.value)}
            />
          </div>
          <div className="tp-modal-field tp-modal-field--half">
            <label className="tp-modal-label">End Date/Time</label>
            <input
              className="tp-modal-input"
              type="datetime-local"
              value={scheduleEnd}
              onChange={(e) => setScheduleEnd(e.target.value)}
            />
          </div>
        </div>

        {/* Assignee */}
        <div className="tp-modal-field">
          <label className="tp-modal-label">Assignee</label>
          <input
            className="tp-modal-input"
            type="text"
            placeholder="User ID or name (comma-separated for multiple)"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
          />
        </div>

        {/* Checklist */}
        <div className="tp-modal-section">
          <div className="tp-modal-section-header">
            <span className="tp-modal-section-title">Checklist</span>
            <button
              type="button"
              className="tp-modal-link-btn"
              onClick={addChecklistItem}
            >
              + Add Item
            </button>
          </div>

          {checklist.length === 0 && (
            <p className="tp-modal-empty">
              No checklist items. Click "+ Add Item" to create one.
            </p>
          )}

          {checklist.map((item, index) => (
            <div key={index} className="tp-modal-checklist-row">
              <input
                className="tp-modal-input tp-modal-input--grow"
                type="text"
                placeholder={`Step ${index + 1}`}
                value={item}
                onChange={(e) => updateChecklistItem(index, e.target.value)}
              />
              <button
                type="button"
                className="tp-modal-item-delete"
                onClick={() => removeChecklistItem(index)}
                title="Remove item"
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        {/* Linked Issues */}
        {linkedIssues.length > 0 && (
          <div className="tp-modal-section">
            <div className="tp-modal-section-header">
              <span className="tp-modal-section-title">
                Linked Issues ({linkedIssues.length})
              </span>
            </div>
            <div className="tp-modal-issues-list">
              {linkedIssues.map((issue) => (
                <div key={issue.issueId} className="tp-modal-issue-item">
                  <span
                    className="tp-modal-severity-dot"
                    data-severity={issue.severity}
                  />
                  <span className="tp-modal-issue-id">{issue.issueId}</span>
                  <span className="tp-modal-issue-category">{issue.category}</span>
                  <span className="tp-modal-issue-asset">
                    {issue.assetPath.inverter ||
                      issue.assetPath.string ||
                      issue.assetPath.station ||
                      '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Impact Preview */}
        {hasImpact && (
          <div className="tp-modal-section tp-modal-section--impact">
            <div className="tp-modal-section-header">
              <span className="tp-modal-section-title">Impact Preview</span>
            </div>
            <div className="tp-modal-impact-grid">
              <div className="tp-modal-impact-card">
                <span className="tp-modal-impact-value">
                  {impact.powerLossW >= 1000
                    ? `${(impact.powerLossW / 1000).toFixed(1)} kW`
                    : `${impact.powerLossW.toFixed(0)} W`}
                </span>
                <span className="tp-modal-impact-label">Power Loss</span>
              </div>
              <div className="tp-modal-impact-card">
                <span className="tp-modal-impact-value">
                  {impact.energyLossKWh.toFixed(1)} kWh
                </span>
                <span className="tp-modal-impact-label">Energy Loss</span>
              </div>
              <div className="tp-modal-impact-card">
                <span className="tp-modal-impact-value">
                  ${impact.revenueLoss.toFixed(2)}
                </span>
                <span className="tp-modal-impact-label">Revenue Loss</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
